import { Badge } from '@/components/ui/badge';

/**
 * How a task renders across the admin surfaces (task list, task tree) given
 * its player visibility.
 *
 * Deactivation cascades: an inactive task hides its whole descendant subtree
 * from players. Admin surfaces therefore have to show two distinct things —
 * the task carrying the flag, which has a toggle an admin can flip, and the
 * branch it drags down with it, which does not. Both read red; the flagged one
 * is the saturated one.
 *
 * Both booleans come from the server. `ancestorInactive` in particular must
 * NOT be re-derived by walking `parentId` on the client: the task list is
 * paginated, so the ancestor is usually not in the page at all.
 */
export interface TaskVisibility {
  /** The task's own `is_active` flag. */
  isActive?: boolean;
  /** True when the task is active but an ancestor is not (server-computed). */
  ancestorInactive?: boolean;
}

/** The task itself is flagged inactive — this is the toggle to flip. */
export function isSelfInactive(task: TaskVisibility): boolean {
  return task.isActive === false;
}

/** Players cannot see this task, whether by its own flag or an ancestor's. */
export function isHiddenFromPlayers(task: TaskVisibility): boolean {
  return isSelfInactive(task) || task.ancestorInactive === true;
}

/**
 * Tone to append to a task-name class string. Empty for a visible task, so
 * the default foreground colour is left untouched.
 */
export function taskNameTone(task: TaskVisibility): string {
  if (isSelfInactive(task)) return ' text-destructive';
  return task.ancestorInactive ? ' text-destructive/70' : '';
}

/**
 * Badge naming WHY a task is hidden, so an admin looking at a red row knows
 * whether this is the task to edit or a symptom of one further up. Renders
 * nothing for a visible task.
 */
export function TaskVisibilityBadge({ task }: { task: TaskVisibility }) {
  if (isSelfInactive(task)) {
    return (
      <Badge variant="destructive" className="text-xs shrink-0">
        Inactive
      </Badge>
    );
  }
  if (task.ancestorInactive) {
    return (
      <Badge
        variant="outline"
        className="text-xs shrink-0 text-destructive/70 border-destructive/40"
      >
        Parent inactive
      </Badge>
    );
  }
  return null;
}
